import React from "react";
import { toast } from "sonner";
import { X, Copy, Download, Mail } from "lucide-react";
import { api } from "../../../software/api";

const MAILBOXES = ["sales@glamourpoolsandspas.com.au", "gary@glamourpoolsandspas.com.au", "paul@glamourpoolsandspas.com.au", "judy@glamourpoolsandspas.com.au", "matt@glamourpoolsandspas.com.au",
  "admin@glamourpoolsandspas.com.au", "justspas@glamourpoolsandspas.com.au", "sales@poolandspaaccessories.com.au", "support@poolandspaaccessories.com.au", "sales@spasnewcastle.com.au", "sales@justspasnewcastle.com.au", "glamourpoolsandspas@bigpond.com"];

export const IT_INSTRUCTIONS = `Subject: Email connection details needed for our new business system (Aqualux Nexus)

Hello,

We are moving our client, supplier and ordering correspondence into our new business system, Aqualux Nexus (a web app). The system needs to READ incoming email and SEND email from our existing mailboxes, so that every email is filed on the right client or supplier record automatically. Outlook will keep working exactly as it does now; nothing is moved or deleted.

MAILBOXES
${MAILBOXES.map((m) => `  - ${m}`).join("\n")}
Please tell us which of these you look after, and whether each one is on Microsoft 365 (Exchange Online) or on your own Exchange / mail server.

----------------------------------------------------------------
PART A: Mailboxes on MICROSOFT 365 / EXCHANGE ONLINE
----------------------------------------------------------------
Please register an application in Microsoft Entra ID (Azure AD):
 1. Entra admin centre > Applications > App registrations > New registration.
    Name: "Aqualux Nexus Mail". Supported account types: this organisation only. No redirect URI needed.
 2. API permissions > Add > Microsoft Graph > APPLICATION permissions:
      - Mail.ReadWrite
      - Mail.Send
      - MailboxSettings.Read
      - User.Read.All (to look up the mailboxes)
    Then click "Grant admin consent".
 3. Certificates & secrets > New client secret (24 months). Copy the secret VALUE.
 4. Recommended: limit the app to ONLY the mailboxes above with an Exchange Online
    Application Access Policy (or RBAC for Applications), e.g.
      New-DistributionGroup -Name "Aqualux Nexus Mailboxes" -Type Security -Members <mailboxes>
      New-ApplicationAccessPolicy -AppId <Application (client) ID> -PolicyScopeGroupId "Aqualux Nexus Mailboxes" -AccessRight RestrictAccess -Description "Aqualux Nexus"
 Please have these ready to enter into the system's secure settings screen (never send secrets by email):
      - Directory (tenant) ID
      - Application (client) ID
      - Client secret VALUE and its expiry date
      - The list of mailboxes covered by this tenant

----------------------------------------------------------------
PART B: Mailboxes on YOUR OWN EXCHANGE SERVER or another mail host
----------------------------------------------------------------
For each mailbox, please enable IMAP and authenticated SMTP, and have these ready to enter into the system's secure settings screen (never send passwords by email):
      - IMAP server name, port (993) and security (SSL/TLS)
      - SMTP server name, port (587 STARTTLS or 465 SSL) and whether SMTP AUTH is enabled
      - Username (often the full email address) and password, or a dedicated app password
      - Confirm the server is reachable from the internet (our system is cloud hosted, so it can't be on a local-only network)
      - Confirm SPF/DKIM allow sending from this server for the domain
 If your server is Exchange with EWS available instead of IMAP, please tell us; we can work with the EWS URL as well.

----------------------------------------------------------------
PART C: glamourpoolsandspas@bigpond.com (Telstra)
----------------------------------------------------------------
 Please confirm IMAP access is on for this account (Telstra: imap.telstra.com port 993 SSL, smtp.telstra.com port 465 SSL)
 and create an app password to enter into the system's secure settings screen. Or, if simpler, set this address to FORWARD a copy of all mail
 to sales@glamourpoolsandspas.com.au.

----------------------------------------------------------------
SECURITY
----------------------------------------------------------------
 - Credentials are typed directly into the system's encrypted secrets store. They are never put in email or chat.
   Please do NOT reply to this email with passwords or secrets. We'll arrange a time to enter them together, by phone or in person.
 - The system only reads and files mail and sends replies we write. It does not delete or move mail in Outlook.
 - Access can be revoked at any time by deleting the app registration (Part A) or changing the password (Part B/C).

Longer term we'd like your advice on consolidating all our mailboxes onto one platform (preferably Microsoft 365).

Thank you,
Kevin Marksteiner
Glamour Pools and Spas
`;

export function ItEmailSetup({ onClose }) {
  const copy = () => navigator.clipboard.writeText(IT_INSTRUCTIONS).then(() => toast.success("Instructions copied")).catch(() => toast.error("Copy failed"));
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([IT_INSTRUCTIONS.replace(/\n/g, "\r\n")], { type: "text/plain" }));
    a.download = "Aqualux Nexus - Email connection instructions for IT.txt"; a.click();
  };
  const email = async () => {
    try { const r = await api("POST", "/it-setup/email-instructions"); toast.success(`Instructions emailed to ${r.sent_to} — forward it to your IT companies`); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="it-email-setup">
      <div className="w-full max-w-3xl bg-white rounded-md shadow-2xl mt-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-3 py-2 bg-[#123a63] text-white rounded-t-md">
          <h3 className="font-black text-sm">Email Connection: Instructions for IT</h3>
          <button onClick={onClose} data-testid="it-email-setup-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex flex-wrap gap-2 px-3 py-2 border-b border-slate-200">
          <button onClick={email} data-testid="it-email-setup-email" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[#123a63] text-white text-[12px] font-bold"><Mail className="h-3.5 w-3.5" />Email to sales@glamourpoolsandspas.com.au</button>
          <button onClick={copy} data-testid="it-email-setup-copy" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-slate-300 text-[12px] font-bold"><Copy className="h-3.5 w-3.5" />Copy</button>
          <button onClick={download} data-testid="it-email-setup-download" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-slate-300 text-[12px] font-bold"><Download className="h-3.5 w-3.5" />Download</button>
        </div>
        <pre className="p-4 text-[12px] leading-snug whitespace-pre-wrap font-mono2 max-h-[70vh] overflow-y-auto" data-testid="it-email-setup-text">{IT_INSTRUCTIONS}</pre>
      </div>
    </div>
  );
}
